import { FunctionComponent, useCallback } from "react";

import { useTranslation } from "react-i18next";

import { faXmark } from "@fortawesome/pro-regular-svg-icons";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Icon } from "@/components/Icon/Icon";

import { DraftCondition, DraftRule } from "./helpers";
import { LanguagePicker } from "./LanguagePicker";
import { LanguageRuleConditionRow } from "./LanguageRuleConditionRow";

interface LanguageRuleRowProps {
	index: number;
	rule: DraftRule;
	onAddCondition: () => void;
	onChangeCondition: (conditionUid: string, updates: Partial<DraftCondition>) => void;
	onRemoveCondition: (conditionUid: string) => void;
	onChangeOutputLanguage: (code: string) => void;
	onRemoveRule: () => void;
}

export const LanguageRuleRow: FunctionComponent<LanguageRuleRowProps> = ({
	index,
	rule,
	onAddCondition,
	onChangeCondition,
	onRemoveCondition,
	onChangeOutputLanguage,
	onRemoveRule,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`messages.languageAutoDetectRules.${key}`);

	const handleConditionChange = useCallback(
		(
			conditionUid: string,
			updates: {
				field?: "profile_language" | "personal_country";
				operator?: "equals" | "is_on";
				value?: string | string[];
			}
		) => {
			onChangeCondition(conditionUid, updates);
		},
		[onChangeCondition]
	);

	const canRemoveCondition = rule.conditions.length > 1;

	return (
		<div className="flex flex-col gap-3 p-4 rounded-lg border border-border-light bg-brand-white">
			<div className="flex items-start justify-between gap-2">
				<span className="text-sm font-semibold text-text w-6 shrink-0">{index + 1}.</span>
				<button
					aria-label={ts("removeRule")}
					className="p-1 rounded hover:bg-background-light text-text-meta transition-colors shrink-0"
					data-testid="remove-rule"
					type="button"
					onClick={onRemoveRule}
				>
					<Icon className="w-4 h-4" icon={faXmark} />
				</button>
			</div>
			<div className="flex flex-col gap-2">
				{rule.conditions.map((condition, cIndex) => (
					<div className="flex flex-col gap-2" key={condition.uid}>
						{cIndex > 0 && (
							<span className="text-xs text-text-meta self-center">{ts("orConnector")}</span>
						)}
						<LanguageRuleConditionRow
							field={condition.field}
							onChange={(updates) => handleConditionChange(condition.uid, updates)}
							onRemove={canRemoveCondition ? () => onRemoveCondition(condition.uid) : undefined}
							operator={condition.operator}
							value={condition.value}
						/>
					</div>
				))}
			</div>
			<div className="flex items-center gap-2">
				<Button
					color={ButtonColor.ACTION_SECONDARY}
					size={ButtonSize.XS}
					title={ts("addCondition")}
					onClick={onAddCondition}
				/>
			</div>
			<div className="flex items-center gap-2 pt-2 border-t border-border-light">
				<span className="text-sm text-text-meta">{ts("thenPick")}</span>
				<LanguagePicker onChange={onChangeOutputLanguage} value={rule.outputLanguage} />
			</div>
		</div>
	);
};
